import * as XLSX from 'xlsx';
import { normalizeSkuFieldForApi } from './skuApiNormalize';

/**
 * Flujo hacia PUT ActualizacionMasivaBulkParcial: actualización masiva parcial.
 * El backend no valida ni exige campos obligatorios del alta; cada ítem puede traer solo uno o varios campos a cambiar.
 * Aquí solo exigimos IdPublicacion (para saber qué publicación tocar) y que exista al menos un campo extra enviable.
 */

const normalizeHeader = (value) =>
    String(value ?? '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .toLowerCase()
        .replace(/[\s_.-]+/g, '');

const boolFromCell = (v) => {
    if (v === null || v === undefined || v === '') return null;
    const raw = String(v).trim().toLowerCase();
    if (!raw) return null;
    if (['1', 'true', 't', 'si', 'sí', 's', 'yes', 'y'].includes(raw)) return true;
    if (['0', 'false', 'f', 'no', 'n'].includes(raw)) return false;
    const n = Number(raw.replace(',', '.'));
    if (!Number.isNaN(n)) return n !== 0;
    return null;
};

const toIntOrNull = (v) => {
    if (v === null || v === undefined || v === '') return null;
    if (typeof v === 'number') return Number.parseInt(String(v), 10);
    const n = Number(String(v).trim().replace(',', '.'));
    return Number.isNaN(n) ? null : Number.parseInt(String(n), 10);
};

const zonaBool = (v) => {
    const n = toIntOrNull(v);
    return n != null && n !== 0;
};

const emptyExcelParseStats = () => ({
    skippedBlank: 0,
    skippedNoId: 0,
    skippedNoMutation: 0
});

const isCellProvided = (row, key) =>
    row[key] !== '' && row[key] != null && String(row[key]).trim() !== '';

/** True si la fila tiene al menos un campo distinto de IdPublicacion para mandar al bulk parcial. */
export function rowHasTipoEnvioBulkUpdatePayload(row) {
    return [
        'observacion',
        'titulo',
        'ean',
        'linkYoutube',
        'descripcionAmpliada',
        'idTipoPublicacion',
        'envio',
        'envioGratis',
        'categoria',
        'marca',
        'idMotivoRevision',
        'observacionesRevision',
        'envioPropio',
        'codigoLegibilidad',
        'garantiaExtActiva',
        'garantiaFabrica',
        'promesaEntrega',
        'amba',
        'interior',
        'patagonia'
    ].some((f) => isCellProvided(row, f));
}

/**
 * Lee la primera hoja del Excel y devuelve solo filas con IdPublicacion y al menos un dato a cambiar.
 * Las filas vacías, sin id o solo con id no entran en `rows` ni en la request.
 *
 * @returns {{ rows: Array, stats: { skippedBlank: number, skippedNoId: number, skippedNoMutation: number } }}
 */
/** Lee .csv o Excel (.xls/.xlsx) y devuelve filas para bulk parcial tipo/envío. */
export async function fileToTipoEnvioWizardRows(file) {
    if (!file) return { rows: [], stats: emptyExcelParseStats() };
    const name = String(file.name ?? '').toLowerCase();
    if (name.endsWith('.csv')) {
        const text = await file.text();
        const wb = XLSX.read(text, { type: 'string', raw: true });
        const buffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        return excelArrayBufferToTipoEnvioWizardRows(buffer);
    }
    const buffer = await file.arrayBuffer();
    return excelArrayBufferToTipoEnvioWizardRows(buffer);
}

export function excelArrayBufferToTipoEnvioWizardRows(buffer) {
    const stats = emptyExcelParseStats();
    const wb = XLSX.read(buffer, { type: 'array' });
    const sheetName = wb.SheetNames[0];
    if (!sheetName) return { rows: [], stats };
    const sheet = wb.Sheets[sheetName];
    const data = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
    if (!Array.isArray(data) || data.length < 2) return { rows: [], stats };

    const headerCells = data[0].map((h) => normalizeHeader(h));
    const out = [];

    for (let i = 1; i < data.length; i += 1) {
        const line = data[i];
        const norm = {};
        headerCells.forEach((h, idx) => {
            const cell = line[idx];
            norm[h] = cell !== undefined && cell !== null ? String(cell).trim() : '';
        });

        const hasAnyCell = Object.keys(norm).some((k) => String(norm[k] ?? '').trim() !== '');
        if (!hasAnyCell) {
            stats.skippedBlank += 1;
            continue;
        }

        const idRaw =
            norm.idpublicacion ??
            norm.idpub ??
            norm.codproducto ??
            norm.codigoproducto ??
            norm.codigo ??
            '';
        const idStr = String(idRaw ?? '').trim();
        const skuSeller = normalizeSkuFieldForApi(norm.skuseller ?? '');
        const skuMarket = normalizeSkuFieldForApi(norm.skumarket ?? norm.skumarkeet ?? norm.sku ?? '');
        if (!idStr && !skuSeller && !skuMarket) {
            stats.skippedNoId += 1;
            continue;
        }

        const candidate = {
            codProducto: idStr,
            skuSeller,
            skuMarket,
            observacion: String(norm.observacion ?? '').trim(),
            titulo: String(norm.titulo ?? '').trim(),
            ean: String(norm.ean ?? '').trim(),
            linkYoutube: String(norm.linkyoutube ?? '').trim(),
            descripcionAmpliada: String(norm.descripcionampliada ?? '').trim(),
            idTipoPublicacion: String(norm.idtipopublicacion ?? '').trim(),
            envio: String(norm.envio ?? norm.tipoentrega ?? '').trim(),
            envioGratis: String(norm.enviogratis ?? '').trim(),
            categoria: String(norm.categoria ?? '').trim(),
            marca: String(norm.marca ?? '').trim(),
            idMotivoRevision: String(norm.idmotivorevision ?? '').trim(),
            observacionesRevision: String(norm.observacionesrevision ?? '').trim(),
            envioPropio: String(norm.enviopropio ?? '').trim(),
            codigoLegibilidad: String(norm.codigolegibilidad ?? '').trim(),
            garantiaExtActiva: String(norm.garantiaextactiva ?? '').trim(),
            garantiaFabrica: String(norm.garantiafabrica ?? '').trim(),
            promesaEntrega: String(norm.promesaentrega ?? '').trim(),
            amba: String(norm.amba ?? '').trim(),
            interior: String(norm.interior ?? '').trim(),
            patagonia: String(norm.patagonia ?? '').trim()
        };

        if (!rowHasTipoEnvioBulkUpdatePayload(candidate)) {
            stats.skippedNoMutation += 1;
            continue;
        }

        out.push(candidate);
    }

    return { rows: out, stats };
}

export function validateTipoEnvioWizardRow(row) {
    const errs = {};
    const idPub = toIntOrNull(row.codProducto);
    const idPubProvided = String(row.codProducto ?? '').trim() !== '';
    const skuSellerProvided = String(row.skuSeller ?? '').trim() !== '';
    const skuMarketProvided = String(row.skuMarket ?? '').trim() !== '';
    if (!idPubProvided && !skuSellerProvided && !skuMarketProvided) {
        errs.codProducto = 'Ingresá al menos un identificador: IdPublicacion, SkuSeller o SkuMarket.';
    } else if (idPubProvided && (!idPub || idPub < 1)) {
        errs.codProducto = 'IdPublicacion inválido.';
    }

    const tipoPubProvided = isCellProvided(row, 'idTipoPublicacion');
    const envioProvided = isCellProvided(row, 'envio');

    if (!rowHasTipoEnvioBulkUpdatePayload(row)) {
        errs.idTipoPublicacion =
            'Indicá al menos un campo a modificar (uno o varios entre tipo publicación, tipo de entrega o zonas).';
    }

    if (tipoPubProvided) {
        const tp = toIntOrNull(row.idTipoPublicacion);
        if (tp == null || tp < 0) errs.idTipoPublicacion = 'IdTipoPublicacion inválido.';
    }
    if (envioProvided) {
        const v = toIntOrNull(row.envio);
        if (v == null || v < 0) errs.envio = 'Tipo de entrega (Envio) inválido.';
    }
    ['categoria', 'marca', 'idMotivoRevision', 'garantiaFabrica', 'promesaEntrega'].forEach((f) => {
        if (!isCellProvided(row, f)) return;
        const v = toIntOrNull(row[f]);
        if (v == null || v < 0) errs[f] = 'Valor numérico inválido.';
    });
    ['amba', 'interior', 'patagonia'].forEach((f) => {
        if (!isCellProvided(row, f)) return;
        const v = toIntOrNull(row[f]);
        if (v == null || v < 0) errs[f] = 'Valor numérico inválido.';
    });
    ['envioGratis', 'envioPropio', 'garantiaExtActiva'].forEach((f) => {
        if (!isCellProvided(row, f)) return;
        if (boolFromCell(row[f]) == null) errs[f] = 'Valor booleano inválido (usar 1/0, true/false, si/no).';
    });
    return errs;
}

/**
 * Payload para PUT api/Publicaciones/ActualizacionMasivaBulkParcial.
 * Solo se incluyen propiedades con valor en la fila; el resto no se envía y el backend conserva lo que ya está.
 */
export function buildActualizacionMasivaBulkParcialItems(rows) {
    return (Array.isArray(rows) ? rows : [])
        .map((r) => {
            const idPub = toIntOrNull(r.codProducto);
            const skuSeller = normalizeSkuFieldForApi(r.skuSeller ?? '');
            const skuMarket = normalizeSkuFieldForApi(r.skuMarket ?? '');
            if ((!idPub || idPub < 1) && !skuSeller && !skuMarket) return null;

            const item = {};
            if (idPub && idPub > 0) item.idPublicacion = idPub;
            if (skuSeller) item.skuSeller = skuSeller;
            if (skuMarket) item.skuMarket = skuMarket;

            ['observacion', 'titulo', 'ean', 'linkYoutube', 'descripcionAmpliada', 'observacionesRevision', 'codigoLegibilidad'].forEach((key) => {
                if (isCellProvided(r, key)) item[key] = String(r[key]).trim();
            });

            if (isCellProvided(r, 'idTipoPublicacion')) {
                const tp = toIntOrNull(r.idTipoPublicacion);
                if (tp != null && tp >= 0) item.idTipoPublicacion = tp;
            }

            if (isCellProvided(r, 'envio')) {
                const te = toIntOrNull(r.envio);
                if (te != null && te >= 0) item.tipoEntrega = te;
            }

            ['categoria', 'marca', 'idMotivoRevision', 'garantiaFabrica', 'promesaEntrega'].forEach((key) => {
                if (!isCellProvided(r, key)) return;
                const value = toIntOrNull(r[key]);
                if (value != null && value >= 0) item[key] = value;
            });
            ['envioGratis', 'envioPropio', 'garantiaExtActiva'].forEach((key) => {
                if (!isCellProvided(r, key)) return;
                const value = boolFromCell(r[key]);
                if (value != null) item[key] = value;
            });

            const zona = {};
            ['amba', 'interior', 'patagonia'].forEach((key) => {
                if (!isCellProvided(r, key)) return;
                zona[key] = zonaBool(r[key]);
            });
            if (Object.keys(zona).length > 0) item.envioGratisZona = zona;

            const identifierKeys = ['idPublicacion', 'skuSeller', 'skuMarket'];
            const hasMutation = Object.keys(item).some((k) => !identifierKeys.includes(k));

            if (!hasMutation) return null;

            return item;
        })
        .filter(Boolean);
}
